// The figure's side of the controller seam (gate A3.2). The controller stream builds the real hero controller in
// parallel; until the merge, the lab drives the Wanderer from a scripted route through the same small interface the
// controller's state will fill: `FigureDriver` gives the root's pose (feet position, yaw) and the `Locomotion` the
// motion reads (speed, velocity, grounded, jump, glide, acting, a look target). At the merge, an adapter reads the
// controller's state into a `FigureDriver` and the lab's script goes.
import type { Locomotion } from "@rpg/figure";

/** What drives a figure each frame: where its root stands and how it moves. */
export interface FigureDriver {
  /** The root's world position (between the feet) and facing (radians about +y; 0 faces +z). */
  pose(): { x: number; y: number; z: number; yaw: number };
  /** The motion's input. */
  locomotion(): Locomotion;
}

/** One leg of the scripted route: a speed held for `seconds`, or a jump (then a glide for `glide` seconds). */
export type RouteLeg = { kind: "move"; speed: number; seconds: number; acting?: boolean } | { kind: "jump"; speed: number; glide: number };

/** The route's tuning (the game's numbers; a stand-in for the controller's feel table until A2 merges). */
export interface RouteTuning {
  /** The loop the hero follows: centre and radii (m), an ellipse in x and z. */
  loop: { x: number; z: number; rx: number; rz: number };
  /** m/s² toward the leg's speed. */
  accel: number;
  /** Jump speed up (m/s), gravity (m/s²), glide sink speed (m/s). */
  jump: number;
  gravity: number;
  sink: number;
  legs: RouteLeg[];
}

/** A scripted route around a loop, stepped on the sim's fixed step, with the ground from `ground(x, z)`. */
export function createRouteDriver(t: RouteTuning, ground: (x: number, z: number) => number) {
  const L = t.loop;
  // the loop's arc length, for a speed in m/s along it
  let perimeter = 0;
  {
    let px = L.x + L.rx;
    let pz = L.z;
    for (let i = 1; i <= 512; i++) {
      const a = (i / 512) * Math.PI * 2;
      const x = L.x + Math.cos(a) * L.rx;
      const z = L.z + Math.sin(a) * L.rz;
      perimeter += Math.hypot(x - px, z - pz);
      px = x;
      pz = z;
    }
  }
  const at = (s: number) => {
    const a = (s / perimeter) * Math.PI * 2;
    const x = L.x + Math.cos(a) * L.rx;
    const z = L.z + Math.sin(a) * L.rz;
    // the tangent (counter-clockwise seen from above is toward +a)
    const tx = -Math.sin(a) * L.rx;
    const tz = Math.cos(a) * L.rz;
    const n = Math.hypot(tx, tz) || 1;
    return { x, z, tx: tx / n, tz: tz / n };
  };

  const st = { s: 0, speed: 0, y: 0, vy: 0, grounded: true, gliding: false, jumped: false, leg: 0, legT: 0, time: 0, hold: false };
  const reset = (): void => {
    Object.assign(st, { s: 0, speed: 0, vy: 0, grounded: true, gliding: false, jumped: false, leg: 0, legT: 0, time: 0 });
    const p = at(0);
    st.y = ground(p.x, p.z);
  };
  reset();

  /** One fixed step of `dt` seconds. */
  const step = (dt: number): void => {
    if (st.hold) return;
    st.time += dt;
    const leg = t.legs[st.leg % t.legs.length]!;
    st.legT += dt;
    st.speed += Math.sign(leg.speed - st.speed) * Math.min(Math.abs(leg.speed - st.speed), t.accel * dt);
    st.s = (st.s + st.speed * dt) % perimeter;
    const p = at(st.s);
    const g = ground(p.x, p.z);
    if (leg.kind === "jump" && !st.jumped) {
      st.jumped = true;
      st.grounded = false;
      st.vy = t.jump;
      st.y = Math.max(st.y, g);
    }
    if (!st.grounded) {
      st.vy -= t.gravity * dt;
      st.gliding = leg.kind === "jump" && st.vy < 0 && st.legT < leg.glide + t.jump / t.gravity;
      if (st.gliding) st.vy = Math.max(st.vy, -t.sink);
      st.y += st.vy * dt;
      if (st.y <= g) {
        st.y = g;
        st.vy = 0;
        st.grounded = true;
        st.gliding = false;
      }
    } else st.y = g;
    const done = leg.kind === "move" ? st.legT >= leg.seconds : st.jumped && st.grounded;
    if (done) {
      st.leg = (st.leg + 1) % t.legs.length;
      st.legT = 0;
      st.jumped = false;
    }
  };

  const driver: FigureDriver & {
    step(dt: number): void;
    reset(): void;
    /** Hold still where it is (shots), or go on. */
    hold(on: boolean): void;
    /** The route's state, for `state()`. */
    state(): { s: number; speed: number; y: number; grounded: boolean; gliding: boolean; leg: number; time: number };
    perimeter: number;
  } = {
    pose() {
      const p = at(st.s);
      return { x: p.x, y: st.y, z: p.z, yaw: Math.atan2(p.tx, p.tz) };
    },
    locomotion() {
      const p = at(st.s);
      const leg = t.legs[st.leg % t.legs.length]!;
      const sp = st.hold ? 0 : st.speed;
      return {
        speed: sp,
        velocity: { x: p.tx * sp, y: st.hold ? 0 : st.vy, z: p.tz * sp },
        grounded: st.grounded,
        jump: !st.grounded && st.vy > 0,
        glide: st.gliding,
        acting: leg.kind === "move" && !!leg.acting,
      };
    },
    step,
    reset,
    hold(on) {
      st.hold = on;
    },
    state: () => ({ s: st.s, speed: st.speed, y: st.y, grounded: st.grounded, gliding: st.gliding, leg: st.leg, time: st.time }),
    perimeter,
  };
  return driver;
}
